@if (!tunnelConnected() && !loading() && !offlineBackupDate()) {
<div class="tunnel-message">
    <p-message severity="warn" [closable]="false" icon="fa-solid fa-plug-circle-xmark mr-2">
        <strong>Device Tunnel Unavailable</strong>
        <p class="mt-1 mb-0 text-sm">The device is not connected via WebSocket tunnel and no property list backup is stored yet.</p>
    </p-message>
    <p-button label="Backups" icon="fa fa-history" [text]="true" severity="info" size="small" (onClick)="showBackupsDialog()" class="mt-2" pTooltip="View stored property list backups"></p-button>
</div>
} @else {
<app-section-card title="Property List Items" subtitle="Values the program owns and persists, kept separately from the config file" icon="fa-solid fa-list" [count]="loading() ? null : items().length" [flush]="true">
    <div actions class="header-actions">
        @if (tunnelConnected()) {
        <p-button
            icon="fa fa-cloud-arrow-up"
            [text]="true"
            severity="success"
            size="small"
            ariaLabel="Back up properties now"
            [loading]="capturing()"
            (onClick)="backupNow()"
            pTooltip="Store a backup of the current property list in the cloud"
        ></p-button>
        }
        <p-button icon="fa fa-history" [text]="true" severity="info" size="small" ariaLabel="View property backups" (onClick)="showBackupsDialog()" pTooltip="View stored property list backups"></p-button>
        @if (!readOnly()) {
        <p-button icon="fa fa-plus" [text]="true" severity="success" size="small" ariaLabel="Add property" (onClick)="showAddDialog()" pTooltip="Add property"></p-button>
        }
        <p-button icon="fa fa-refresh" [text]="true" severity="info" size="small" ariaLabel="Refresh properties" [loading]="loading()" (onClick)="loadData()" pTooltip="Refresh properties"></p-button>
    </div>

    @if (offlineBackupDate() || errorMessage()) {
    <div class="app-card-pad card-messages">
        @if (offlineBackupDate(); as backupDate) {
        <p-message severity="info" [closable]="false" icon="fa-solid fa-clock-rotate-left mr-2" class="w-full"> <strong>Device offline</strong> — showing backup from {{ backupDate | date: 'medium' }} (read-only) </p-message>
        } @if (errorMessage(); as err) {
        <p-message severity="error" [closable]="false" class="w-full">{{ err }}</p-message>
        }
    </div>
    }

    <p-table
        [value]="items()"
        [loading]="loading()"
        [paginator]="true"
        [rows]="15"
        [rowsPerPageOptions]="[10, 15, 25, 50]"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} properties"
        [rowHover]="true"
        [globalFilterFields]="['key', 'value', 'type']"
        sortField="key"
        [sortOrder]="1"
    >
        <ng-template #header>
            <tr>
                <th pSortableColumn="key" style="width: 25%">
                    Key
                    <p-sorticon field="key"></p-sorticon>
                </th>
                <th pSortableColumn="type" style="width: 10%">
                    Type
                    <p-sorticon field="type"></p-sorticon>
                </th>
                <th>Value</th>
                @if (!readOnly()) {
                <th style="width: 120px">Actions</th>
                }
            </tr>
        </ng-template>

        <ng-template #body let-item>
            <tr>
                <td>
                    <code class="key-code">{{ item.key }}</code>
                </td>
                <td>
                    <p-tag [value]="item.type" [severity]="getTypeSeverity(item.type)"></p-tag>
                </td>
                <td class="value-cell">
                    <span class="value-text" [title]="formatValueForDisplay(item.value, item.type)"> {{ formatValueForDisplay(item.value, item.type) }} </span>
                </td>
                @if (!readOnly()) {
                <td>
                    <div class="action-buttons">
                        <p-button icon="fa fa-pencil" [text]="true" severity="warn" size="small" ariaLabel="Edit property" (onClick)="showEditDialog(item)" pTooltip="Edit property"></p-button>
                        <p-button icon="fa fa-trash" [text]="true" severity="danger" size="small" ariaLabel="Delete property" (onClick)="confirmDelete(item)" pTooltip="Delete property"></p-button>
                    </div>
                </td>
                }
            </tr>
        </ng-template>

        <ng-template #emptymessage>
            <tr>
                <td colspan="4">
                    <app-empty-state icon="fa-solid fa-list" title="No property list items found" [actionLabel]="readOnly() ? null : 'Add your first property'" actionIcon="fa fa-plus" (action)="showAddDialog()" />
                </td>
            </tr>
        </ng-template>
    </p-table>
</app-section-card>
}

<p-dialog [header]="dialogMode === 'add' ? 'Add Property' : 'Edit Property'" [modal]="true" [(visible)]="dialogVisible" [style]="{ width: '500px' }" [closable]="true">
    <div class="dialog-content">
        <div class="field">
            <label for="key">Key</label>
            <input pInputText id="key" [(ngModel)]="editingItem.key" [disabled]="dialogMode === 'edit'" class="w-full" placeholder="Enter property key" />
        </div>

        <div class="field">
            <label for="type">Type</label>
            <p-select id="type" [options]="typeOptions" [(ngModel)]="editingItem.type" optionLabel="label" optionValue="value" class="w-full" [disabled]="dialogMode === 'edit'" appendTo="body"></p-select>
        </div>

        <div class="field">
            <label for="value">Value</label>
            @if (editingItem.type === 'object' || editingItem.type === 'array') {
            <textarea pTextarea id="value" [(ngModel)]="editingItem.value" rows="8" class="w-full font-mono" placeholder="Enter JSON value"></textarea>
            } @else if (editingItem.type === 'boolean') {
            <p-select
                id="value"
                [options]="[
                        { label: 'True', value: 'true' },
                        { label: 'False', value: 'false' }
                    ]"
                [(ngModel)]="editingItem.value"
                optionLabel="label"
                optionValue="value"
                class="w-full"
                appendTo="body"
            ></p-select>
            } @else {
            <input pInputText id="value" [(ngModel)]="editingItem.value" class="w-full" [placeholder]="editingItem.type === 'number' ? 'Enter number' : 'Enter value'" />
            }
        </div>
    </div>

    <ng-template #footer>
        <p-button label="Cancel" severity="secondary" (onClick)="dialogVisible = false"></p-button>
        <p-button [label]="dialogMode === 'add' ? 'Add' : 'Save'" (onClick)="saveItem()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog header="Property List Backups" [modal]="true" [(visible)]="backupsDialogVisible" [style]="{ width: '720px' }" [closable]="true">
    <p class="text-sm mb-3">Daily backups of the device's property list. The last 7 distinct versions are kept.</p>
    <p-table [value]="backupVersions()" [loading]="backupsLoading()" class="p-datatable-striped p-datatable-sm">
        <ng-template #header>
            <tr>
                <th>Created</th>
                <th>Last Seen</th>
                <th style="width: 80px">Items</th>
                <th style="width: 160px">Actions</th>
            </tr>
        </ng-template>
        <ng-template #body let-version let-i="rowIndex">
            <tr>
                <td>
                    {{ version.createdUtc | date: 'medium' }} @if (i === 0) {
                    <p-tag value="latest" severity="success" class="ml-2"></p-tag>
                    }
                </td>
                <td>{{ version.lastSeenUtc | date: 'medium' }}</td>
                <td>{{ version.itemCount }}</td>
                <td>
                    <div class="action-buttons">
                        <p-button icon="fa fa-eye" [text]="true" severity="info" size="small" ariaLabel="View backup entries" (onClick)="viewBackupVersion(version)" pTooltip="View entries"></p-button>
                        <p-button icon="fa fa-download" [text]="true" severity="success" size="small" ariaLabel="Download backup as JSON" (onClick)="downloadBackupVersion(version)" pTooltip="Download as JSON"></p-button>
                        <p-button
                            icon="fa fa-history"
                            (onClick)="confirmRestore(version)"
                            [disabled]="!tunnelConnected() || restoring()"
                            [loading]="restoring()"
                            [text]="true"
                            severity="danger"
                            size="small"
                            ariaLabel="Restore backup"
                            [pTooltip]="tunnelConnected() ? 'Restore this backup to the device' : 'Device must be online to restore'"
                        ></p-button>
                    </div>
                </td>
            </tr>
        </ng-template>
        <ng-template #emptymessage>
            <tr>
                <td colspan="4" class="text-center">
                    <div class="empty-message">
                        <i class="fa-solid fa-info-circle"></i>
                        <p>No backups stored yet</p>
                    </div>
                </td>
            </tr>
        </ng-template>
    </p-table>
</p-dialog>

<p-dialog [header]="'Backup from ' + ((viewedVersion()?.meta?.createdUtc | date: 'medium') ?? '')" [modal]="true" [(visible)]="versionViewerVisible" [style]="{ width: '640px' }" [closable]="true">
    @if (viewedVersion(); as viewed) {
    <p-table [value]="viewed.items" class="p-datatable-striped p-datatable-sm" [scrollable]="true" scrollHeight="400px">
        <ng-template #header>
            <tr>
                <th style="width: 30%">Key</th>
                <th style="width: 15%">Type</th>
                <th>Value</th>
            </tr>
        </ng-template>
        <ng-template #body let-item>
            <tr>
                <td>
                    <code class="key-code">{{ item.key }}</code>
                </td>
                <td>
                    <p-tag [value]="item.type" [severity]="getTypeSeverity(item.type)"></p-tag>
                </td>
                <td class="value-cell">
                    <span class="value-text" [title]="formatValueForDisplay(item.value, item.type)">{{ formatValueForDisplay(item.value, item.type) }}</span>
                </td>
            </tr>
        </ng-template>
        <ng-template #emptymessage>
            <tr>
                <td colspan="3" class="text-center">This backup is empty</td>
            </tr>
        </ng-template>
    </p-table>
    <div class="flex justify-content-end gap-2 mt-3">
        <p-button label="Download" icon="fa fa-download" severity="secondary" (onClick)="downloadBackupVersion(viewed.meta)"></p-button>
        <p-button
            label="Restore"
            icon="fa fa-history"
            severity="danger"
            [disabled]="!tunnelConnected() || restoring()"
            [loading]="restoring()"
            (onClick)="confirmRestore(viewed.meta)"
            [pTooltip]="tunnelConnected() ? '' : 'Device must be online to restore'"
        ></p-button>
    </div>
    }
</p-dialog>

<p-confirmdialog></p-confirmdialog>
